import { useHotkey } from "@/shared/foundation/primitives";

import type { StepDirection } from "./layout";
import type { TimelineActions } from "./useTimelineState";

/**
 * Matches an open popup whose keys belong to it, not the timeline: arrow keys
 * move through a menu or listbox, and keys pressed in a dialog (the cluster
 * popover, the jump form) are its own.
 */
const POPUP =
  '[role="menu"], [role="menubar"], [role="listbox"], [role="dialog"], [role="alertdialog"]';

export interface TimelineHotkeyOptions
  extends Pick<
    TimelineActions<unknown>,
    "zoomIn" | "zoomOut" | "pan" | "fitAll"
  > {
  /** Registers the keys only while true; off, nothing listens. */
  enabled: boolean;
  /**
   * Selects the previous (`-1`) or next (`1`) item. `Timeline` passes the
   * function its previous / next buttons call, so a key press and a click
   * land on the same item.
   */
  step: (direction: StepDirection) => void;
}

/**
 * Page-wide keyboard shortcuts for one timeline: ← / → select the previous
 * / next item through `step`, Shift+← / → pan, + / − zoom, F fits the whole
 * history. They listen on the document, so they work wherever focus is on
 * the page — the timeline is the page's main object, and a user who has just
 * clicked a list row or the detail card can keep stepping. They leave other
 * controls' keys alone: bare keys and Shift combinations do nothing while
 * focus is in a text field (the hotkey library's default), and every key does
 * nothing while focus is inside an open menu, listbox or dialog, such as a
 * filter menu. A key the timeline handles has its default prevented, so an
 * arrow does not also scroll the page. Enter and Escape are not here: a bar is
 * a button and the cluster popover is a dialog, so both are native.
 */
export function useTimelineHotkeys({
  enabled,
  step,
  zoomIn,
  zoomOut,
  pan,
  fitAll,
}: TimelineHotkeyOptions): void {
  // The library calls preventDefault before invoking the handler, which would
  // cancel a menu's arrow keys too; onPage calls it only after the popup check.
  const options = { enabled, preventDefault: false };
  const onPage = (action: () => void) => (event: KeyboardEvent) => {
    if (event.target instanceof Element && event.target.closest(POPUP)) {
      return;
    }
    event.preventDefault();
    action();
  };
  useHotkey(
    "ArrowLeft",
    onPage(() => step(-1)),
    options,
  );
  useHotkey(
    "ArrowRight",
    onPage(() => step(1)),
    options,
  );
  useHotkey(
    "Shift+ArrowLeft",
    onPage(() => pan(-1)),
    options,
  );
  useHotkey(
    "Shift+ArrowRight",
    onPage(() => pan(1)),
    options,
  );
  // "+" is Shift+= on most layouts, and "=" alone zooms in too, as it does
  // for browser page zoom. The hotkey library splits every binding on "+",
  // so "+" itself cannot be bound: a numpad "+" does nothing.
  useHotkey("=", onPage(zoomIn), options);
  useHotkey({ key: "=", shift: true }, onPage(zoomIn), options);
  useHotkey("-", onPage(zoomOut), options);
  useHotkey("F", onPage(fitAll), options);
}
